/**
 * Hook useProductQuery
 * 
 * Versão otimizada com React Query para buscar um produto específico
 */

import { useQuery } from '@tanstack/react-query';
import { getProductById } from '../services/api';
import { handleError } from '../utils/errorHandler';

/**
 * @param {string} productId - ID do produto
 * @param {Object} options - Opções do React Query
 * @returns {Object} { product, loading, error, refetch, ...queryResult }
 */
export const useProductQuery = (productId, options = {}) => {
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutos
    ...queryOptions
  } = options;

  const query = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      if (!productId) return null;
      return await getProductById(productId);
    },
    enabled: enabled && !!productId,
    staleTime,
    retry: 2,
    ...queryOptions,
  });

  // Tratamento de erro centralizado
  if (query.error) {
    handleError(query.error, {
      context: 'useProductQuery',
      showToast: false,
    });
  }

  return {
    product: query.data || null,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    ...query,
  };
};

export default useProductQuery;

